/**
 * useScrapes Hook (NEW Architecture - Generic)
 * 
 * Thin wrapper around useList for scrapes.
 * All database operations through ScrapeRepository.
 * Zero Firestore imports - fully database-agnostic!
 */

import { useMemo } from 'react';
import { useList } from './useList';
import { ScrapeRepository } from '../models/ScrapeRepository';
import { db, functions } from '../firebase';

/**
 * Hook for fetching and managing scrape lists
 * 
 * @param {Object} options - Filter and pagination options
 * @param {string} options.statusFilter - Filter by scrape status ('all', 'pending', 'running', 'completed', 'failed')
 * @param {string} options.scraperId - Filter by scraper ID
 * @param {string} options.mailshotId - Filter by mailshot ID
 * @param {string} options.sortOrder - Sort order ('asc' or 'desc')
 * @param {number} options.pageSize - Number of items per page (default: 100)
 * @param {boolean} options.realtime - Enable real-time updates (default: true)
 * 
 * @returns {Object} { scrapes, loading, error, refresh, count, isEmpty }
 */
export const useScrapes = (options = {}) => {
  const {
    statusFilter = 'all',
    scraperId = null,
    mailshotId = null,
    sortOrder = 'desc',
    pageSize = 100,
    realtime = true
  } = options;

  // Create repository instance
  const repository = useMemo(() => new ScrapeRepository(db, functions), []);

  // Build query options for repository
  const queryOptions = useMemo(() => {
    const whereConstraints = [];

    // Status filter
    if (statusFilter !== 'all') {
      whereConstraints.push(['status', '==', statusFilter]);
    }

    // Scraper filter
    if (scraperId) {
      whereConstraints.push(['createdBy', '==', scraperId]);
    }

    // Mailshot filter
    if (mailshotId) {
      whereConstraints.push(['mailshotId', '==', mailshotId]);
    }

    return {
      where: whereConstraints.length > 0 ? whereConstraints : undefined,
      orderBy: 'createdAt',
      orderDirection: sortOrder,
      limit: pageSize
    };
  }, [statusFilter, scraperId, mailshotId, sortOrder, pageSize]);

  // Use generic list hook
  const {
    entities: scrapes,
    loading,
    error,
    refresh,
    count,
    isEmpty
  } = useList({
    repository,
    options: queryOptions,
    realtime,
    immediate: true
  });

  return {
    scrapes,
    loading,
    error,
    refresh,
    count,
    isEmpty,
    hasError: !!error
  };
};
